/**
 * SEO 유틸리티 모듈 - 네이버 SearchAdvisor 가이드 100% 충족
 *
 * 기능:
 * 1. 이미지 alt 속성 자동 생성
 * 2. 내부 링크 구조 강화
 * 3. 메타태그 검사 및 교정
 * 4. 콘텐츠 길이/키워드 밀도 분석
 * 5. SEO 점수 계산
 */
import { normalizeTypeName } from './typeName';

// ========== 타입 정의 ==========

export interface SEOImageAlt {
  propertyName?: string;
  imageType: 'main' | 'business' | 'brand' | 'location' | 'premium' | 'community' |
             'layout' | 'floorplan' | 'exterior' | 'system' | 'design' | 'landscape' | 'general';
  customSuffix?: string;
}

export interface SEOInternalLink {
  text: string;
  href: string;
  title?: string;
}

export interface SEOAnalysisResult {
  score: number;
  issues: string[];
  recommendations: string[];
  keywordDensity: number;
  contentLength: number;
}

export interface PropertySEOData {
  title: string;
  address?: string;
  developer?: string;
  status?: string;
  minPrice?: number | null;
  maxPrice?: number | null;
  minArea?: number | null;
  maxArea?: number | null;
  totalUnits?: number | null;
  propertyType?: string;
}

// ========== 이미지 ALT 자동 생성 ==========

const IMAGE_TYPE_LABELS: Record<SEOImageAlt['imageType'], string> = {
  main: '대표 이미지',
  business: '사업개요',
  brand: '브랜드소개',
  location: '입지환경',
  premium: '프리미엄',
  community: '커뮤니티',
  layout: '단지배치도',
  floorplan: '평면도',
  exterior: '외관',
  system: '시스템',
  design: '단지설계',
  landscape: '조경시설',
  general: '이미지',
};

/**
 * 이미지 alt 속성 자동 생성
 *
 * @param options - alt 생성 옵션
 * @returns 생성된 alt 텍스트
 *
 * @example
 * generateImageAlt({ propertyName: '힐스테이트 구미', imageType: 'main' })
 * // => "힐스테이트 구미 분양정보 대표 이미지"
 */
export function generateImageAlt(options: SEOImageAlt): string {
  const { propertyName, imageType, customSuffix } = options;
  const typeLabel = IMAGE_TYPE_LABELS[imageType] || '이미지';

  if (propertyName) {
    const suffix = customSuffix ? ` ${customSuffix}` : '';
    return `${propertyName} ${typeLabel}${suffix}`;
  }

  return `분양정보 ${typeLabel}`;
}

/**
 * 평면도 이미지 alt 생성
 *
 * @param propertyName - 분양현장명
 * @param typeName - 평면도 타입 (예: "84A", "59B")
 * @param area - 전용면적
 * @returns 생성된 alt 텍스트
 */
export function generateFloorPlanAlt(
  propertyName: string,
  typeName: string,
  area?: number
): string {
  const areaText = area ? ` ${area}㎡` : '';
  return `${propertyName} ${normalizeTypeName(typeName)}타입${areaText} 평면도`;
}

// ========== 내부 링크 생성 ==========

/**
 * SEO 내부 링크 생성
 * 네이버 SearchAdvisor 가이드에 따른 키워드 포함 앵커텍스트
 *
 * @param region - 지역명
 * @param propertyId - 현재 분양현장 ID (제외용)
 * @returns 내부 링크 목록
 */
export function generateInternalLinks(region?: string, propertyId?: number): SEOInternalLink[] {
  const links: SEOInternalLink[] = [
    {
      text: '전체 분양현장 보기',
      href: '/list',
      title: '부냥 전체 신규분양 현장 목록',
    },
    {
      text: '지도에서 분양현장 찾기',
      href: '/',
      title: '지도에서 분양현장 위치 확인',
    },
    {
      text: '분양중인 현장 보기',
      href: '/list?status=분양중',
      title: '현재 분양중인 신규분양 현장',
    },
  ];

  if (region) {
    links.push({
      text: `${region} 분양정보 더보기`,
      href: `/list?region=${encodeURIComponent(region)}`,
      title: `${region} 지역 신규분양 현장`,
    });
  }

  return links;
}

/**
 * 관련 분양현장 링크 생성 (같은 지역)
 */
export function generateRelatedPropertyLinks(
  region: string,
  currentId: number,
  relatedProperties: Array<{ id: number; title: string; house_manage_no?: string | null }>
): SEOInternalLink[] {
  return relatedProperties
    .filter(p => p.id !== currentId)
    .slice(0, 5)
    .map(p => ({
      text: `${p.title} 분양정보`,
      href: p.house_manage_no ? `/property/${p.house_manage_no}` : `/property/${p.id}`,
      title: `${p.title} 분양가, 청약일정, 모델하우스 정보`,
    }));
}

// ========== 콘텐츠 분석 ==========

/**
 * 텍스트 키워드 밀도 계산
 *
 * @param text - 분석할 텍스트
 * @param keyword - 주요 키워드
 * @returns 키워드 밀도 (0~100%)
 */
export function calculateKeywordDensity(text: string, keyword: string): number {
  if (!text || !keyword) return 0;

  const cleanText = text.toLowerCase().replace(/[^\w\s가-힣]/g, '');
  const words = cleanText.split(/\s+/).filter(Boolean);
  const keywordCount = words.filter(w => w.includes(keyword.toLowerCase())).length;

  return words.length > 0 ? (keywordCount / words.length) * 100 : 0;
}

/**
 * 콘텐츠 길이 체크 (네이버 권장: 최소 1,200자)
 *
 * @param text - 분석할 텍스트
 * @returns 길이 분석 결과
 */
export function analyzeContentLength(text: string): {
  length: number;
  isOptimal: boolean;
  recommendation: string;
} {
  const length = text.replace(/\s/g, '').length;
  const minLength = 1200;
  const optimalLength = 2000;

  if (length >= optimalLength) {
    return {
      length,
      isOptimal: true,
      recommendation: '콘텐츠 길이가 최적입니다.',
    };
  } else if (length >= minLength) {
    return {
      length,
      isOptimal: true,
      recommendation: `콘텐츠 길이가 적정합니다. ${optimalLength - length}자 추가 시 SEO 점수 향상`,
    };
  } else {
    return {
      length,
      isOptimal: false,
      recommendation: `최소 ${minLength - length}자 추가 필요 (현재 ${length}자, 권장 ${minLength}자 이상)`,
    };
  }
}

// ========== SEO 콘텐츠 자동 생성 ==========

/**
 * SEO 최적화된 분양정보 텍스트 생성 (1,500자 이상)
 *
 * @param data - 분양현장 데이터
 * @returns 생성된 SEO 텍스트
 */
export function generateSEOContent(data: PropertySEOData): string {
  const {
    title,
    address,
    developer,
    status,
    minPrice,
    maxPrice,
    minArea,
    maxArea,
    totalUnits,
    propertyType = '아파트',
  } = data;

  // 지역 추출
  const addressParts = address?.split(' ') || [];
  const region = addressParts.slice(0, 2).join(' ') || '전국';
  const city = addressParts[0] || '';

  // 가격 포맷팅
  const formatPrice = (price: number | null | undefined): string => {
    if (!price) return '';
    if (price >= 10000) {
      const billion = Math.floor(price / 10000);
      const remainder = price % 10000;
      return remainder > 0 ? `${billion}억 ${remainder.toLocaleString()}만원` : `${billion}억원`;
    }
    return `${price.toLocaleString()}만원`;
  };

  const priceRange = minPrice && maxPrice
    ? `${formatPrice(minPrice)}~${formatPrice(maxPrice)}`
    : '분양가 문의';

  const areaRange = minArea && maxArea
    ? `${minArea}㎡~${maxArea}㎡`
    : '다양한 타입';

  const unitsText = totalUnits ? `총 ${totalUnits.toLocaleString()}세대` : '대단지';

  // SEO 콘텐츠 생성 (1,500자 이상)
  const content = `
${title}은(는) ${address || region}에 위치한 ${propertyType} 분양 현장입니다.
${developer ? `${developer}에서 시공하는 ` : ''}${title}은(는) ${unitsText} 규모로 조성됩니다.

${title} 분양가는 ${priceRange}이며, 전용면적 ${areaRange}으로 구성되어 있습니다.
현재 ${status || '분양 진행 중'}인 ${title}은(는) ${region} 지역에서 높은 관심을 받고 있는 신축 ${propertyType}입니다.

${title} 모델하우스 방문을 원하시면 1533-8124로 연락주시거나, 온라인 상담 신청을 해주세요.
${title}의 분양일정, 청약 정보, 입지 분석, 평면도 등 상세 정보를 부냥에서 확인하실 수 있습니다.

${title}의 주요 특징으로는 우수한 입지환경, 편리한 교통, 다양한 커뮤니티 시설을 갖추고 있습니다.
${region} 지역은 교통, 교육, 생활 인프라가 잘 갖춰진 지역으로, ${title}은(는) 이러한 입지적 장점을 최대한 활용할 수 있습니다.

${title}은(는) 최신 트렌드를 반영한 설계와 스마트홈 시스템을 적용하여 주거 편의성을 높였습니다.
단지 내 조경과 커뮤니티 시설은 입주민들의 삶의 질을 향상시키는 데 기여할 것입니다.

${city} ${propertyType} 분양을 찾고 계신다면 ${title}을(를) 주목해 주세요.
${title} 분양정보, 분양가, 청약일정, 입주시기 등 모든 정보를 부냥에서 확인하실 수 있습니다.

${region} 신규분양 ${title}, 지금 바로 부냥에서 분양정보를 확인하고 방문 예약을 신청하세요.
  `.trim();

  return content;
}

// ========== SEO 분석 ==========

/**
 * 페이지 SEO 점수 분석
 *
 * @param options - 분석 옵션
 * @returns SEO 분석 결과
 */
export function analyzeSEO(options: {
  title: string;
  description: string;
  content: string;
  keyword: string;
  hasH1: boolean;
  hasStructuredData: boolean;
  imagesWithAlt: number;
  imagesWithoutAlt: number;
  internalLinks: number;
}): SEOAnalysisResult {
  const issues: string[] = [];
  const recommendations: string[] = [];
  let score = 100;

  // 제목 검사 (10점)
  if (options.title.length < 30) {
    issues.push('제목이 너무 짧습니다 (권장: 30-60자)');
    score -= 5;
  } else if (options.title.length > 70) {
    issues.push('제목이 너무 깁니다 (권장: 30-60자)');
    score -= 3;
  }

  // 설명 검사 (10점)
  if (options.description.length < 100) {
    issues.push('메타 설명이 너무 짧습니다 (권장: 100-160자)');
    score -= 5;
  } else if (options.description.length > 160) {
    issues.push('메타 설명이 너무 깁니다 (권장: 100-160자)');
    score -= 3;
  }

  // 콘텐츠 길이 검사 (15점)
  const contentAnalysis = analyzeContentLength(options.content);
  if (!contentAnalysis.isOptimal) {
    issues.push(contentAnalysis.recommendation);
    score -= 15;
  }

  // 키워드 밀도 검사 (10점)
  const keywordDensity = calculateKeywordDensity(options.content, options.keyword);
  if (keywordDensity < 1.0) {
    recommendations.push('키워드 밀도가 낮습니다 (권장: 1.0-1.4%)');
    score -= 5;
  } else if (keywordDensity > 2.5) {
    issues.push('키워드 과다 사용 (권장: 1.0-1.4%)');
    score -= 10;
  }

  // H1 검사 (10점)
  if (!options.hasH1) {
    issues.push('H1 태그가 없습니다');
    score -= 10;
  }

  // 구조화 데이터 검사 (15점)
  if (!options.hasStructuredData) {
    recommendations.push('JSON-LD 구조화 데이터 추가 권장');
    score -= 15;
  }

  // 이미지 alt 검사 (15점)
  if (options.imagesWithoutAlt > 0) {
    issues.push(`alt 속성이 없는 이미지 ${options.imagesWithoutAlt}개`);
    score -= Math.min(15, options.imagesWithoutAlt * 3);
  }

  // 내부 링크 검사 (15점)
  if (options.internalLinks < 3) {
    recommendations.push('내부 링크 추가 권장 (최소 3개)');
    score -= 10;
  }

  return {
    score: Math.max(0, score),
    issues,
    recommendations,
    keywordDensity,
    contentLength: contentAnalysis.length,
  };
}

// ========== URL 표준화 ==========

/**
 * Canonical URL 생성
 */
export function generateCanonicalUrl(path: string): string {
  const baseUrl = 'https://voonang.com';
  // 파라미터 URL 처리 - noindex 대상 파라미터 제거
  const noindexParams = ['ref', 'utm', 'source', 'track', 'utm_source', 'utm_medium', 'utm_campaign'];
  const url = new URL(path, baseUrl);

  noindexParams.forEach(param => url.searchParams.delete(param));

  return url.toString();
}

/**
 * noindex 필요 여부 확인
 */
export function shouldNoindex(path: string): boolean {
  const noindexPatterns = [
    /[?&](ref|utm|source|track|utm_source|utm_medium|utm_campaign)=/,
    /\/auth\//,
    /\/login/,
    /\/profile/,
    /\/admin/,
  ];

  return noindexPatterns.some(pattern => pattern.test(path));
}

// ========== 요약 정보 생성 ==========

/**
 * 분양현장 요약 정보 생성 (테이블용)
 */
export function generatePropertySummary(data: PropertySEOData): Array<{ label: string; value: string }> {
  const formatPrice = (price: number | null | undefined): string => {
    if (!price) return '-';
    if (price >= 10000) {
      return `${Math.floor(price / 10000)}억${price % 10000 > 0 ? ` ${(price % 10000).toLocaleString()}만` : ''}원`;
    }
    return `${price.toLocaleString()}만원`;
  };

  return [
    { label: '분양가', value: data.minPrice && data.maxPrice ? `${formatPrice(data.minPrice)} ~ ${formatPrice(data.maxPrice)}` : '문의' },
    { label: '전용면적', value: data.minArea && data.maxArea ? `${data.minArea}㎡ ~ ${data.maxArea}㎡` : '-' },
    { label: '총 세대수', value: data.totalUnits ? `${data.totalUnits.toLocaleString()}세대` : '-' },
    { label: '분양유형', value: data.propertyType || '-' },
    { label: '분양상태', value: data.status || '-' },
    { label: '시행사', value: data.developer || '-' },
  ];
}

/**
 * 주요 포인트 3줄 요약 생성
 */
export function generateKeyPoints(data: PropertySEOData): string[] {
  const points: string[] = [];

  // 가격 포인트
  if (data.minPrice && data.maxPrice) {
    const avgPrice = Math.round((data.minPrice + data.maxPrice) / 2);
    if (avgPrice < 30000) {
      points.push('합리적인 분양가로 실수요자에게 적합');
    } else if (avgPrice < 50000) {
      points.push('중견 브랜드의 프리미엄 단지');
    } else {
      points.push('대형 브랜드 프리미엄 아파트');
    }
  }

  // 규모 포인트
  if (data.totalUnits) {
    if (data.totalUnits >= 1000) {
      points.push(`${data.totalUnits.toLocaleString()}세대 대단지 커뮤니티 시설`);
    } else if (data.totalUnits >= 500) {
      points.push('중대형 단지로 생활 인프라 우수');
    } else {
      points.push('프라이빗한 소규모 단지');
    }
  }

  // 입지 포인트
  const addressParts = data.address?.split(' ') || [];
  if (addressParts.length >= 2) {
    points.push(`${addressParts[0]} ${addressParts[1]} 핵심 입지`);
  }

  return points.slice(0, 3);
}

export default {
  generateImageAlt,
  generateFloorPlanAlt,
  generateInternalLinks,
  generateRelatedPropertyLinks,
  calculateKeywordDensity,
  analyzeContentLength,
  generateSEOContent,
  analyzeSEO,
  generateCanonicalUrl,
  shouldNoindex,
  generatePropertySummary,
  generateKeyPoints,
};
